/**
 * AdvisorLandingGate — Einmal-pro-Tab-Session-Redirect eines Advisors
 * (Nutzer mit ≥1 echter `advisor`-Membership in `availableTenants`) vom
 * `/dashboard`-Index ins Kanzlei-Cockpit `/dashboard/mandanten`
 * (M-ACF-2, `architecture-advisor-cockpit-first.md` §3.1 / §3.2 / §7.3 / §18).
 *
 * Wrappt AUSSCHLIESSLICH das `/dashboard`-Index-Element (außen um
 * `AdvisorHidden`, §7.4). Alle anderen Routen bleiben ungewrappt — ein
 * Deep-Link auf eine beliebige andere Dashboard-Route wird nie umgebogen, und
 * ein späterer `/dashboard`-Besuch nach Deep-Link-Einstieg ist durch den
 * MainLayout-Stempel immunisiert.
 *
 * Kern-Mechanik:
 *  - `seenAtMount`: EINMAL pro Mount via `useState`-Initializer eingefroren
 *    (StrictMode-fest, §3.1). Der spätere eigene Stempel-Effekt immunisiert den
 *    laufenden Mount NICHT — der Redirect kann also auch feuern, wenn die
 *    advisor-Membership erst nach dem asynchronen Tenant-Load eintrifft.
 *  - Redirect = Render-Ergebnis (`<Navigate/>`, Muster `AdvisorHidden`), kein
 *    Effekt ⇒ kein Doppel-Feuer-Risiko.
 *  - Zielgruppe = ausschließlich echte `role:'advisor'`-Memberships, bewusst
 *    NICHT `useIsAdvisorContext()` (§3.2): reine Invite-Empfänger ohne Mandat
 *    würden sonst auf einer leeren Mandantenliste landen.
 *
 * Inert-Garantie (Flag off): `featureOn` ist false ⇒ nur `<>{children}</>`, die
 * Storage-Helper sind zusätzlich intern flag-geguarded (0 Storage-Calls).
 *
 * @module components/advisor/AdvisorLandingGate
 */

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ADVISOR_LANDING_ENABLED } from '../../config/advisorLandingAccess';
import { isKanzleiAccount } from '../../config/kanzleiAccess';
import {
  markAdvisorLandingSeen,
  isAdvisorLandingSeen,
  setAdvisorMembershipHint,
  clearAdvisorMembershipHint,
  hasAdvisorMembershipHint,
} from '../../utils/advisorLandingSession';

// Muster `TenantSwitcher.tsx:12` — Modul-Ladezeit-Read des Shell-Flags. Die
// Wirksamkeit des Landings ist zusätzlich an `UX_SHELL_ENABLED` geknüpft (§3.3).
const UX_SHELL_ENABLED = process.env.REACT_APP_UX_SHELL_ENABLED === 'true';

export interface AdvisorLandingGateProps {
  children: React.ReactNode;
}

const AdvisorLandingGate: React.FC<AdvisorLandingGateProps> = ({ children }) => {
  const { currentUser, availableTenants, sellerData } = useAuth();

  const uid = currentUser?.uid ?? null;
  const featureOn = ADVISOR_LANDING_ENABLED && UX_SHELL_ENABLED && uid != null;

  // F1-Fix (Review-Re-Loop): defensive Ableitung statt direktem Zugriff auf
  // `availableTenants` — härtet die Inert-Garantie im GATE-CODE selbst ab
  // (nicht nur im AuthContext-Kontrakt jedes Consumers). Bei Flag off wird
  // keinerlei Kontext-Ableitung benötigt (Flag-Fast-Return unten), diese Zeile
  // schadet aber auch dann nicht (reine Fallback-Normalisierung, kein Hook).
  const tenants = availableTenants ?? [];

  // Mount-Snapshot: EINMAL eingefroren, immun gegen den späteren Stempel-Effekt.
  // Kein uid ⇒ `true` (nie Redirect). Storage-Helper ist fail-closed (Fehler ⇒
  // true ⇒ kein Redirect).
  const [seenAtMount] = useState<boolean>(() =>
    uid == null ? true : isAdvisorLandingSeen(uid),
  );

  // Stempel den Marker beim Mount (idempotent). Flag off ⇒ Helper ist no-op.
  useEffect(() => {
    if (featureOn && uid != null) {
      markAdvisorLandingSeen(uid);
    }
  }, [featureOn, uid]);

  // F1-Fix: `featureOn &&`-Kurzschluss zuerst — bei Flag off wird die
  // Tenant-Liste gar nicht erst ausgewertet (`.some()` entfällt), keine
  // bedingten Hooks nötig (reine Werte, keine Hook-Aufrufe betroffen).
  const tenantsLoaded = featureOn && tenants.length > 0; // zuverlässig per I12
  const isAdvisor = featureOn && tenants.some((tenant) => tenant.role === 'advisor');

  // M-OB-14 (§8.4): W3-Self-Service — eine frische Kanzlei ohne Mandat
  // (accountType==='kanzlei') wird ebenfalls auf die Mandanten-Landing
  // (Stepper) umgeleitet. `featureOn &&`-Kurzschluss zuerst ⇒ bei Flag off wird
  // `sellerData` gar nicht ausgewertet (Inert-Garantie/0-Storage-Calls, AC6).
  // Braucht KEIN `tenantsLoaded` (basiert auf sellerData, nicht auf der
  // Tenant-Liste) — ein reiner Owner-Kanzlei-Account hat i.d.R. keine
  // advisor-Membership.
  const isKanzlei = featureOn && isKanzleiAccount(sellerData);

  // Hint-Pflege im Effekt (nicht im Render): setzen, sobald eine
  // advisor-Membership beobachtet wird; self-healing löschen, wenn die geladene
  // Liste keine mehr enthält (E14).
  useEffect(() => {
    if (!featureOn || uid == null || !tenantsLoaded) return;
    if (isAdvisor) {
      setAdvisorMembershipHint(uid);
    } else {
      clearAdvisorMembershipHint(uid);
    }
  }, [featureOn, tenantsLoaded, isAdvisor, uid]);

  if (featureOn && !seenAtMount) {
    if ((tenantsLoaded && isAdvisor) || isKanzlei) {
      return <Navigate to="/dashboard/mandanten" replace />;
    }
    // Flash-Hold: Tenants noch nicht geladen, aber der Hint deutet auf eine
    // advisor-Membership ⇒ das Formular NICHT aufblitzen lassen. Endet
    // deterministisch mit dem Load (Redirect oder Children). Fail-open (Fehler
    // ⇒ kein Hold ⇒ höchstens ein kurzer Flash).
    if (!tenantsLoaded && uid != null && hasAdvisorMembershipHint(uid)) {
      return null;
    }
  }

  return <>{children}</>;
};

export default AdvisorLandingGate;
